/**
 * useFansData — fetches GET /api/v1/dashboard/fans.
 *
 * NO mock fallback per CEO directive 2026-04-28: every visible value is live
 * or the section is hidden / replaced with "Not available" copy.
 */
import { useEffect, useState, useCallback } from "react";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:4000/api/v1";
const FETCH_TIMEOUT_MS = 8000;

function withTimeout(promise, ms, controller) {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      controller.abort();
      reject(new Error(`timeout after ${ms}ms`));
    }, ms);
    promise.then(
      (v) => { clearTimeout(timer); resolve(v); },
      (e) => { clearTimeout(timer); reject(e); },
    );
  });
}

export function useFansData(range = null, rangeId = null, refreshKey = 0) {
  const [data, setData]     = useState(null);
  const [error, setError]   = useState(null);
  const [loading, setLoading] = useState(true);

  const rangeKey = range ? `${range.from || ''}|${range.to || ''}|${rangeId || ''}` : '';

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    const controller = new AbortController();
    try {
      const token  = (typeof localStorage !== "undefined") ? localStorage.getItem("fc_token") : null;
      const apiKey = process.env.REACT_APP_API_KEY;
      const headers = {};
      if (token)       headers.Authorization = `Bearer ${token}`;
      else if (apiKey) headers["X-API-Key"]  = apiKey;
      const params = new URLSearchParams();
      if (range && range.from) params.set('from', range.from);
      if (range && range.to)   params.set('to',   range.to);
      // Backend uses rangeId for apples-to-apples MTD/WTD/YTD comparisons.
      if (rangeId) params.set('rangeId', rangeId);
      const qs = params.toString();
      const res = await withTimeout(
        fetch(`${API_BASE}/dashboard/fans${qs ? `?${qs}` : ''}`, {
          headers,
          signal: controller.signal,
        }),
        FETCH_TIMEOUT_MS,
        controller,
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      if (!json || !json.success || !json.data) throw new Error("malformed response envelope");
      setData(json.data);
    } catch (e) {
      setError(e.message || String(e));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [rangeKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [refreshKey, rangeKey]);

  return { data, error, loading, reload: load };
}
